import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../config';
import axios from 'axios';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonText,
  IonIcon,
  IonButton,
  IonSpinner
} from '@ionic/react';
import { 
  arrowBackOutline, 
  barbellOutline,
  informationCircleOutline,
  personOutline,
  keyOutline
} from 'ionicons/icons';
import LifestyleTools from './LifestyleTools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LifestyleToolsTestHarness = () => {
  const history = useHistory();
  const [auth_token, setAuthToken] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showLifestyleTools, setShowLifestyleTools] = useState(false);
  const [error, setError] = useState(null);

  // Load members when auth token is provided
  const loadMembers = useCallback(async () => {
    if (!auth_token.trim()) return;
    
    setLoadingMembers(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${config.apiUrl}/members_names`,
        {
          headers: {
            'Authorization': `Bearer ${auth_token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Sort members by Surname, First_name
      const sortedMembers = response.data.sort((a, b) => {
        const surnameA = a.Surname || '';
        const surnameB = b.Surname || '';
        if (surnameA !== surnameB) {
          return surnameA.localeCompare(surnameB);
        }
        const firstNameA = a.First_name || '';
        const firstNameB = b.First_name || '';
        return firstNameA.localeCompare(firstNameB);
      });
      
      setMembers(sortedMembers);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members. Please check your auth token.');
    } finally {
      setLoadingMembers(false);
    }
  }, [auth_token]);

  // Handle member selection
  const handleMemberSelect = (memberId) => {
    setSelectedMember(memberId);
    if (memberId) {
      setShowLifestyleTools(true);
    }
  };

  // Load members when auth token changes
  useEffect(() => {
    if (auth_token.trim()) {
      loadMembers();
    } else {
      setMembers([]);
      setSelectedMember('');
      setShowLifestyleTools(false);
    }
  }, [auth_token, loadMembers]);

  if (showLifestyleTools && selectedMember) {
    return (
      <>
        <LifestyleTools 
          member_id={selectedMember} 
          auth_token={auth_token}
          user_id={null}
        />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButton fill="clear" slot="start" onClick={() => history.goBack()}>
            <IonIcon icon={arrowBackOutline} />
          </IonButton>
          <IonTitle>Lifestyle Tools Test Harness</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px' }}>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={barbellOutline} />
                Lifestyle Tools Test Harness
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>This test harness allows you to test the Lifestyle Tools (Exercise Plan Generator and Diet Generator) with real member data.</p>
                <p>Enter your auth token and select a member to generate personalized 14-day plans.</p>
              </IonText>
            </IonCardContent>
          </IonCard>

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Authentication</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonIcon icon={keyOutline} slot="start" />
                <IonLabel position="stacked">Auth Token *</IonLabel>
                <IonInput
                  value={auth_token}
                  onIonInput={e => setAuthToken(e.detail.value)}
                  placeholder="Enter your auth token"
                  type="text"
                  clearInput
                />
              </IonItem>
            </IonCardContent>
          </IonCard>

          {error && (
            <IonCard color="danger">
              <IonCardContent>
                <IonText color="light">
                  <IonIcon icon={informationCircleOutline} />
                  {error}
                </IonText>
              </IonCardContent>
            </IonCard>
          )}

          {auth_token && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Select Member</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {loadingMembers ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <IonSpinner name="crescent" />
                    <IonText>Loading members...</IonText>
                  </div>
                ) : (
                  <IonItem>
                    <IonIcon icon={personOutline} slot="start" />
                    <IonLabel position="stacked">Member *</IonLabel>
                    <IonSelect
                      value={selectedMember}
                      onIonChange={e => handleMemberSelect(e.detail.value)}
                      placeholder="Select a member"
                      interface="popover"
                    >
                      {members.map(member => (
                        <IonSelectOption key={member.id} value={member.id.toString()}>
                          {member.Surname}, {member.First_name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                )}
              </IonCardContent>
            </IonCard>
          )}

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <IonButton 
              expand="block" 
              onClick={() => history.push('/lifestyle-tools')}
              className="launch-button"
            >
              <IonIcon icon={barbellOutline} slot="start" />
              Launch Lifestyle Tools (Manual Mode)
            </IonButton>
          </div>
        </div>
      </IonContent>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </IonPage>
  );
};

export default LifestyleToolsTestHarness;



